import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShoppingBag } from "lucide-react";
import { siteContent } from "@/data/site-content";
import Layout from "@/components/layout/Layout";
import HeroSection from "@/components/sections/HeroSection";
import VideoBanner from "@/components/sections/VideoBanner";
import ProductGrid from "@/components/shop/ProductGrid";
import AnimateOnScroll from "@/components/AnimateOnScroll";
import {
  ShopFilters,
  type ShopCategoryFilter,
  type ShopSort,
} from "@/components/shop/ShopFilters";
import { Seo } from "@/components/Seo";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { handleApiError } from "@/lib/errors";
import { listActiveProducts, type Product } from "@/lib/shop";

const CATEGORY_LABEL: Record<ShopCategoryFilter, string> = {
  alle: "Alle",
  mennesker: "Til deg",
  hunder: "Til hunden din",
};

function parseCategory(value: string | null): ShopCategoryFilter {
  if (value === "mennesker" || value === "hunder") return value;
  return "alle";
}

function parseSort(value: string | null): ShopSort {
  if (value === "pris-stigende" || value === "pris-synkende") return value;
  return "nyeste";
}

const Nettbutikk = () => {
  const page = siteContent.pages.find((p) => p.slug === "nettbutikk");
  const seo = siteContent.seo["nettbutikk"];
  const hero = page?.sections.find((s) => s.type === "hero");

  const [params, setParams] = useSearchParams();
  const category = parseCategory(params.get("kategori"));
  const sort = parseSort(params.get("sort"));
  const query = params.get("q") ?? "";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    data: products = [],
    isLoading: loading,
    error,
  } = useQuery<Product[]>({
    queryKey: ["shop", "products"],
    queryFn: () => listActiveProducts(),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (error) {
      handleApiError(error, {
        source: "Nettbutikk.listActiveProducts",
        fallbackMessage: "Kunne ikke laste produktene. Prøv igjen senere.",
      });
    }
  }, [error]);

  const filtered = useMemo(() => {
    let result = products;
    if (category !== "alle") {
      result = result.filter((p) => p.category === category);
    }
    const trimmed = query.trim().toLowerCase();
    if (trimmed) {
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(trimmed) ||
          (p.description ?? "").toLowerCase().includes(trimmed),
      );
    }
    if (sort === "pris-stigende") {
      result = [...result].sort(
        (a, b) => (a.variants[0]?.priceOre ?? 0) - (b.variants[0]?.priceOre ?? 0),
      );
    } else if (sort === "pris-synkende") {
      result = [...result].sort(
        (a, b) => (b.variants[0]?.priceOre ?? 0) - (a.variants[0]?.priceOre ?? 0),
      );
    }
    return result;
  }, [products, category, sort, query]);

  const updateParam = (key: string, value: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  const resetFilters = () => {
    setParams(new URLSearchParams(), { replace: true });
  };

  const hasActiveFilter =
    category !== "alle" || sort !== "nyeste" || query.trim().length > 0;

  const dynamicTitle =
    category === "alle"
      ? (seo?.title ?? "Nettbutikk – Håp Naturlab")
      : `${CATEGORY_LABEL[category]} – Nettbutikk – Håp Naturlab`;
  const dynamicDescription =
    category === "alle"
      ? (seo?.description ??
        "Håndlaget økologisk hudpleie der hele overskuddet går til syke barn.")
      : category === "hunder"
        ? "Naturlig pleie for hunden din — hele overskuddet går til syke barn."
        : "Naturlig hudpleie til deg — hele overskuddet går til syke barn.";

  return (
    <Layout>
      <Seo
        title={dynamicTitle}
        description={dynamicDescription}
        path="nettbutikk"
        noIndex={hasActiveFilter}
      />
      {hero && (
        <HeroSection
          heading={hero.heading}
          body={hero.body}
          image={hero.image}
          localImage={hero.localImage}
          cta={hero.cta}
        />
      )}

      <div className="mx-auto max-w-6xl px-6 pt-10">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Hjem</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {category === "alle" ? (
                <BreadcrumbPage>Nettbutikk</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to="/nettbutikk">Nettbutikk</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {category !== "alle" && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{CATEGORY_LABEL[category]}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <VideoBanner
        sources={[
          { src: "/videos/hero-nettbutikk.webm", type: "video/webm" },
          { src: "/videos/hero-nettbutikk.mp4", type: "video/mp4" },
        ]}
        poster="/videos/hero-nettbutikk-poster.jpg"
      />

      <section
        id="produkter"
        className="mx-auto max-w-6xl px-6 py-10 md:py-14 scroll-mt-24"
      >
        <AnimateOnScroll>
          <h2 className="mb-8 text-center font-heading text-3xl font-bold md:text-4xl">
            Våre produkter
          </h2>
        </AnimateOnScroll>

        <ShopFilters
          category={category}
          sort={sort}
          query={query}
          onCategoryChange={(next) =>
            updateParam("kategori", next === "alle" ? null : next)
          }
          onSortChange={(next) =>
            updateParam("sort", next === "nyeste" ? null : next)
          }
          onQueryChange={(next) => updateParam("q", next)}
          resultCount={filtered.length}
        />

        <div className="mt-10">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center">
              <ShoppingBag className="mx-auto h-16 w-16 text-muted-foreground/40" />
              <h3 className="mt-4 font-heading text-xl font-bold">
                Ingen produkter ennå
              </h3>
              <p className="mt-2 text-muted-foreground font-body">
                Produktene våre kommer snart! Følg med.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <ShoppingBag className="mx-auto h-16 w-16 text-muted-foreground/40" />
              <h3 className="mt-4 font-heading text-xl font-bold">
                Ingen produkter matcher valgene
              </h3>
              <p className="mt-2 text-muted-foreground font-body">
                Prøv å endre filter, sortering eller søk.
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground font-body shadow-md transition-all hover:opacity-90"
              >
                Nullstill filter
              </button>
            </div>
          ) : (
            <ProductGrid products={filtered} />
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Nettbutikk;
